<template>
    <v-container class="mt-10 pb-12"
        style="min-width: 1200px; max-width: 1600px; padding-left: 24px; padding-right: 24px;">
        <!-- Header Section -->
        <div class="overview-header mb-6">
            <div class="d-flex align-center justify-space-between mb-4">
                <div>
                    <h1 class="text-h4 font-weight-bold mb-2">Manager's Hub</h1>
                    <p class="text-subtitle-1 grey--text mb-0">Monitor team performance, distribute work, and coach your people</p>
                </div>
                <div class="d-flex flex-wrap" style="gap: 8px;">
                    <v-btn color="primary" outlined :loading="refreshing" @click="refreshData" class="text-none">
                        <v-icon left>mdi-refresh</v-icon>
                        Refresh
                    </v-btn>
                    <v-btn color="primary" @click="openCreateTaskDialog" class="text-none">
                        <v-icon left>mdi-plus</v-icon>
                        Create Task
                    </v-btn>
                </div>
            </div>

            <!-- Statistics Cards -->
            <v-row class="mb-6">
                <v-col cols="12" sm="6" md="3">
                    <v-card class="pa-4 text-center position-relative" elevation="1">
                        <v-overlay :value="statsLoading" absolute>
                            <v-progress-circular indeterminate size="32" width="2" color="primary" />
                        </v-overlay>
                        <div class="d-flex align-center justify-center mb-2">
                            <v-icon class="mr-2" color="primary">mdi-format-list-bulleted</v-icon>
                            <span class="text-caption text-uppercase font-weight-bold">Total Tasks</span>
                        </div>
                        <div class="text-h3 font-weight-bold">{{ totalTasks }}</div>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-card class="pa-4 text-center position-relative" elevation="1">
                        <v-overlay :value="statsLoading" absolute>
                            <v-progress-circular indeterminate size="32" width="2" color="orange" />
                        </v-overlay>
                        <div class="d-flex align-center justify-center mb-2">
                            <v-icon class="mr-2" color="orange">mdi-progress-clock</v-icon>
                            <span class="text-caption text-uppercase font-weight-bold">In Progress</span>
                        </div>
                        <div class="text-h3 font-weight-bold">{{ inProgressTasks }}</div>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-card class="pa-4 text-center position-relative" elevation="1">
                        <v-overlay :value="statsLoading" absolute>
                            <v-progress-circular indeterminate size="32" width="2" color="success" />
                        </v-overlay>
                        <div class="d-flex align-center justify-center mb-2">
                            <v-icon class="mr-2" color="success">mdi-check-circle</v-icon>
                            <span class="text-caption text-uppercase font-weight-bold">Completed</span>
                        </div>
                        <div class="text-h3 font-weight-bold">{{ completedTasks }}</div>
                    </v-card>
                </v-col>
                <v-col cols="12" sm="6" md="3">
                    <v-card class="pa-4 text-center position-relative" elevation="1">
                        <v-overlay :value="statsLoading" absolute>
                            <v-progress-circular indeterminate size="32" width="2" color="error" />
                        </v-overlay>
                        <div class="d-flex align-center justify-center mb-2">
                            <v-icon class="mr-2" color="error">mdi-alert-circle</v-icon>
                            <span class="text-caption text-uppercase font-weight-bold">Overdue</span>
                        </div>
                        <div class="text-h3 font-weight-bold">{{ overdueTasks }}</div>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Filters -->
            <v-card elevation="0" outlined class="mb-6 filter-card">
                <v-card-text>
                    <v-row>
                        <!-- Search -->
                        <v-col cols="12" md="3">
                            <v-text-field v-model="searchQuery" placeholder="Search tasks..." outlined dense
                                hide-details prepend-inner-icon="mdi-magnify" clearable />
                        </v-col>

                        <!-- Teams Filter -->
                        <v-col cols="12" md="2">
                            <v-select v-model="selectedRoles" :items="rolesSelect" item-text="name" item-value="id"
                                label="All Teams" outlined dense multiple hide-details clearable>
                                <template v-slot:selection="{ item, index }">
                                    <v-chip v-if="index === 0" small>
                                        <span>{{ item.name }}</span>
                                    </v-chip>
                                    <span v-if="index === 1" class="grey--text text-caption">
                                        (+{{ selectedRoles.length - 1 }} others)
                                    </span>
                                </template>
                            </v-select>
                        </v-col>

                        <!-- Status Filter -->
                        <v-col cols="12" md="2">
                            <v-select v-model="selectedStatuses" :items="statusesSelect" item-text="name"
                                item-value="id" label="All Status" outlined dense multiple hide-details clearable>
                                <template v-slot:selection="{ item, index }">
                                    <v-chip v-if="index === 0" small>
                                        <span>{{ item.name }}</span>
                                    </v-chip>
                                    <span v-if="index === 1" class="grey--text text-caption">
                                        (+{{ selectedStatuses.length - 1 }} others)
                                    </span>
                                </template>
                            </v-select>
                        </v-col>

                        <!-- People Filter -->
                        <v-col cols="12" md="2">
                            <v-select v-model="selectedUsers" :items="usersSelect" item-text="name" item-value="id"
                                label="All People" outlined dense multiple hide-details clearable>
                                <template v-slot:selection="{ item, index }">
                                    <v-chip v-if="index === 0" small>
                                        <span>{{ item.name }}</span>
                                    </v-chip>
                                    <span v-if="index === 1" class="grey--text text-caption">
                                        (+{{ selectedUsers.length - 1 }} others)
                                    </span>
                                </template>
                            </v-select>
                        </v-col>

                        <!-- Companies Filter -->
                        <v-col cols="12" md="2">
                            <v-select v-model="selectedCompanies" :items="companiesSelect" item-text="name"
                                item-value="id" label="All Companies" outlined dense multiple hide-details clearable>
                                <template v-slot:selection="{ item, index }">
                                    <v-chip v-if="index === 0" small>
                                        <span>{{ item.name }}</span>
                                    </v-chip>
                                    <span v-if="index === 1" class="grey--text text-caption">
                                        (+{{ selectedCompanies.length - 1 }} others)
                                    </span>
                                </template>
                            </v-select>
                        </v-col>

                        <!-- Due Date Filter -->
                        <v-col cols="12" md="1">
                            <v-select v-model="selectedDueDateFilter" :items="dueDateFilters" item-text="name"
                                item-value="value" label="Due Date (Earliest)" outlined dense hide-details clearable />
                        </v-col>
                    </v-row>

                    <!-- Clear Filters -->
                    <div v-if="hasActiveFilters" class="mt-3">
                        <v-btn text small color="primary" @click="clearAllFilters">
                            <v-icon left small>mdi-filter-remove</v-icon>
                            Clear Filters
                        </v-btn>
                    </div>
                </v-card-text>
            </v-card>
        </div>

        <!-- Team Cards Grid -->
        <div class="teams-container">
            <!-- Loading state for role data pre-loading (admin only) -->
            <div v-if="roleDataLoading" class="text-center pa-12">
                <v-progress-circular indeterminate size="64" width="4" color="primary" />
                <div class="mt-4 text-h6 grey--text">Loading teams...</div>
                <div class="mt-2 text-caption grey--text">Please wait while we prepare your overview</div>
            </div>
            <!-- Loading state for tasks -->
            <div v-else-if="tasksLoading" class="text-center pa-8">
                <v-progress-circular indeterminate size="50" color="primary" />
                <div class="mt-3 text-subtitle-1 grey--text">Loading teams...</div>
            </div>

            <!-- Teams Grid -->
            <div v-else class="teams-grid">
                <div v-for="role in filteredRoles" :key="role.id" class="team-card-wrapper">
                    <v-card class="team-card" elevation="2" hover @click="navigateToTeam(role.id)">
                        <!-- Team Header -->
                        <div class="team-card-header">
                            <div class="d-flex align-center">
                                <v-avatar class="mr-3" size="48" :color="getTeamColor(role.id)">
                                    <v-icon dark size="28">{{ getTeamIcon(role.id) }}</v-icon>
                                </v-avatar>
                                <div class="flex-grow-1">
                                    <h3 class="team-card-title mb-1">{{ role.name }}</h3>
                                    <p class="team-card-subtitle mb-0">
                                        <template v-if="role.managers && role.managers.length > 0">
                                            <v-icon x-small class="mr-1">mdi-account-tie</v-icon>
                                            <span v-if="role.managers.length === 1">
                                                {{ role.managers[0]?.manager?.name || 'Unknown' }}
                                            </span>
                                            <span v-else>
                                                {{ role.managers.length }} Managers
                                            </span>
                                        </template>
                                        <template v-else>
                                            <span class="text--secondary">No managers assigned</span>
                                        </template>
                                    </p>
                                </div>
                                <v-chip small class="status-chip active-chip">Active</v-chip>
                            </div>
                        </div>

                        <!-- Team Stats -->
                        <v-divider></v-divider>
                        <div class="team-card-stats pa-4">
                            <div class="stat-row">
                                <span class="stat-label">Tasks in progress</span>
                                <span class="stat-value">{{ getTeamInProgressCount(role.id) }}</span>
                            </div>
                            <div class="stat-row">
                                <span class="stat-label">All tasks</span>
                                <span class="stat-value">{{ getTeamTaskCount(role.id) }}</span>
                            </div>
                        </div>

                        <!-- Progress Bar -->
                        <div class="team-card-progress px-4 pb-4">
                            <div class="progress-header mb-2">
                                <span class="progress-label">Progress</span>
                                <span class="progress-percentage">{{ getTeamProgress(role.id) }}%</span>
                            </div>
                            <div class="progress-bar-container">
                                <div class="progress-bar-fill" :style="{ width: getTeamProgress(role.id) + '%' }"></div>
                            </div>
                        </div>

                    </v-card>
                </div>
            </div>

            <!-- Empty State -->
            <div v-if="!roleDataLoading && !tasksLoading && filteredRoles.length === 0" class="empty-state-container">
                <v-icon size="64" color="#E0E0E0">mdi-account-group</v-icon>
                <h3 class="empty-title mt-4">No teams found</h3>
                <p class="empty-description">Adjust your filters or create a new team</p>
            </div>
        </div>

        <!-- New Enhanced Task Creation Dialog -->
        <TaskCreationDialog 
            v-model="create_task_dialog" 
            :users="users()" 
            :companies="companies()" 
            :default-assignees="selected_role_managers"
            task-type="assign"
            @tasks-created="onTasksCreated" 
            @close="closeCreateTaskDialog" 
        />

        <!-- Manager Assignment Dialog -->
        <v-dialog v-model="managerDialog" max-width="500px" persistent>
            <v-card>
                <v-card-title class="headline">
                    <v-icon left>mdi-account-tie</v-icon>
                    Assign Manager
                    <v-spacer></v-spacer>
                    <v-btn icon @click="closeManagerDialog">
                        <v-icon>mdi-close</v-icon>
                    </v-btn>
                </v-card-title>
                <v-divider></v-divider>
                <v-card-text class="pt-6">
                    <v-autocomplete
                        v-model="selectedManagerIds"
                        :items="usersSelect"
                        item-text="name"
                        item-value="id"
                        label="Select Managers"
                        outlined
                        multiple
                        chips
                        deletable-chips
                        clearable
                        prepend-inner-icon="mdi-account-search"
                        :hint="selectedRoleName() ? `Assigning managers for ${selectedRoleName()}` : ''"
                        persistent-hint
                        @change="console.log('[autocomplete @change] selectedManagerIds:', selectedManagerIds)"
                    >
                        <template v-slot:selection="{ item }">
                            <v-chip
                                small
                                color="primary"
                                text-color="white"
                                close
                                class="ma-1"
                                @click:close="removeManager(item.id)"
                            >
                                <v-avatar left small>
                                    <span style="font-size: 10px;">{{ getInitials(item.name) }}</span>
                                </v-avatar>
                                {{ item.name }}
                            </v-chip>
                        </template>
                        <template v-slot:item="{ item }">
                            <v-list-item-avatar>
                                <v-avatar color="primary" size="32">
                                    <span class="white--text text-caption">{{ getInitials(item.name) }}</span>
                                </v-avatar>
                            </v-list-item-avatar>
                            <v-list-item-content>
                                <v-list-item-title>{{ item.name }}</v-list-item-title>
                                <v-list-item-subtitle v-if="item.email">{{ item.email }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </template>
                    </v-autocomplete>
                </v-card-text>
                <v-divider></v-divider>
                <v-card-actions class="pa-4">
                    <v-spacer></v-spacer>
                    <v-btn text @click="closeManagerDialog">
                        Cancel
                    </v-btn>
                    <v-btn
                        color="primary"
                        :disabled="selectedManagerIds.length === 0"
                        @click="saveManagers"
                    >
                        <v-icon left small>mdi-check</v-icon>
                        Assign Managers
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>

    </v-container>
</template>

<script>
import { mapGetters } from "vuex";
import TaskCreationDialog from "../modules/TaskCreationDialog.vue";

export default {
    name: "OverviewComponent",
    inject: [
        'users',
        'refreshUsers',
        'overviewTasks',
        'overviewStats',
        'refreshOverviewTasks',
        'updateOverviewStats',
        'companies',
        'refreshCompanies',
        'allManagers',
        'refreshManagers',
        'roleTasksMap',
        'refreshRoleTasksMap',
    ],
    components: {
        TaskCreationDialog,
    },
    data() {
        return {
            loading: false,
            refreshing: false,
            sendingOverdueReminders: false,
            statsLoading: true,
            tasksLoading: true,
            roleDataLoading: true, // Track if role tasks are being pre-loaded
            searchQuery: '',
            selectedRoles: [],
            selectedStatuses: [],
            selectedUsers: [],
            selectedCompanies: [],
            selectedDueDateFilter: null,
            expandedPanels: {},
            roleManagers: {}, // Store role_id -> user_id mappings
            localRoleTasksMap: {}, // Store role_id -> tasks[] mappings (local state for non-admin)
            loadingRoles: {}, // Track which roles are currently loading
            renderingRoles: {}, // Track which roles are currently rendering (delayed)
            tasksPerPage: 50, // Limit initial render

            // Create task dialog
            create_task_dialog: false,
            selected_role_for_task: null,
            selected_role_managers: [],

            // Manager assignment dialog
            managerDialog: false,
            selectedRoleId: null,
            selectedManagerIds: [],

            dueDateFilters: [
                { name: 'All', value: null },
                { name: 'Overdue', value: 'overdue' },
                { name: 'Due Today', value: 'today' },
                { name: 'Due This Week', value: 'week' },
                { name: 'Due This Month', value: 'month' },
            ],
        };
    },
    watch: {},
    async mounted() {
        document.title = "DG Tasks - Manager's Hub";

        console.log('[OverviewComponent] Mounted - checking injected providers...');
        console.log('[OverviewComponent] refreshOverviewTasks:', typeof this.refreshOverviewTasks);
        console.log('[OverviewComponent] overviewTasks:', typeof this.overviewTasks);

        // Check if data is already loaded from DefaultLayout
        const hasOverviewTasks = (this.overviewTasks() || []).length > 0;
        const hasBasicData = this.get_statuses && this.get_statuses.length > 0;

        console.log('[OverviewComponent] hasOverviewTasks:', hasOverviewTasks);
        console.log('[OverviewComponent] hasBasicData:', hasBasicData);

        // Set initial loading states
        this.statsLoading = !hasBasicData;
        this.tasksLoading = !hasOverviewTasks;

        // Load basic data first (non-blocking for UI)
        const dataLoadPromises = [];

        if (!this.get_priorities || this.get_priorities.length === 0) {
            dataLoadPromises.push(this.getPriorities());
        }
        if (!this.get_statuses || this.get_statuses.length === 0) {
            dataLoadPromises.push(this.getStatus());
        }
        if (!this.users() || this.users().length === 0) {
            dataLoadPromises.push(this.loadUsers());
        }
        if (!this.companies() || this.companies().length === 0) {
            dataLoadPromises.push(this.refreshCompanies());
        }

        // Wait for basic data to load
        if (dataLoadPromises.length > 0) {
            await Promise.all(dataLoadPromises);
        }

        this.statsLoading = false;

        // Load role managers
        await this.loadRoleManagers();

        // For admin users, wait for role tasks to be pre-loaded
        const isAdmin = [1, 11].includes(this.get_user?.role_id);
        if (isAdmin) {
            console.log('[OverviewComponent] Admin user - waiting for role tasks pre-load');
            // Wait for roleTasksMap to be populated (max 10 seconds)
            const startTime = Date.now();
            while (Object.keys(this.roleTasksMap()).length === 0 && (Date.now() - startTime) < 10000) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
            console.log('[OverviewComponent] Role tasks loaded:', Object.keys(this.roleTasksMap()).length, 'roles');
        }

        // Tasks will be loaded on-demand when expansion panels are clicked
        this.tasksLoading = false;
        this.roleDataLoading = false;

        // Pre-render optimization: Expand all panels to trigger rendering, then collapse
        // This eliminates lag on subsequent user expansions
        await this.$nextTick();
        await this.preRenderAllPanels();
    },
    beforeDestroy() {
        // Cleanup if needed
    },
    beforeRouteEnter(to, from, next) {
        // This runs before the route is confirmed
        next();
    },
    beforeRouteLeave(to, from, next) {
        // This runs when the user is leaving this page
        next();
    },
    computed: {
        ...mapGetters({
            get_user: "auth/get_user",
            get_roles: "auth/get_roles",
            get_priorities: "tasks/get_priorities",
            get_statuses: "tasks/get_statuses",
            get_overview_stats: "tasks/getOverviewStats",
        }),

        // Filter options
        rolesSelect() {
            return this.get_roles || [];
        },
        statusesSelect() {
            return this.get_statuses || [];
        },
        usersSelect() {
            return this.users() || [];
        },
        companiesSelect() {
            return this.companies() || [];
        },

        // Filtered roles based on search and filters
        filteredRoles() {
            // Don't show roles until data is loaded
            if (!this.get_roles?.length) {
                return [];
            }

            let roles = this.get_roles || [];

            // Merge members data into roles
            const managers = this.allManagers() || [];
            roles = roles.map(role => {
                // Find all members for this role (user_role_id matches role.id)
                const roleMembers = managers.filter(m => m.user_role_id === role.id);
                return {
                    ...role,
                    managers: roleMembers
                };
            });

            if (this.selectedRoles.length > 0) {
                roles = roles.filter(role => this.selectedRoles.includes(role.id));
            }

            return roles;
        },

        // Filtered tasks
        filteredTasks() {
            const tasks = this.overviewTasks() || [];
            if (!Array.isArray(tasks)) {
                return [];
            }
            let filtered = [...tasks];

            // Search filter
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                filtered = filtered.filter(task =>
                    task.name?.toLowerCase().includes(query) ||
                    task.description?.toLowerCase().includes(query)
                );
            }

            // Status filter
            if (this.selectedStatuses.length > 0) {
                filtered = filtered.filter(task =>
                    this.selectedStatuses.includes(task.status_id)
                );
            }

            // Users filter
            if (this.selectedUsers.length > 0) {
                filtered = filtered.filter(task => {
                    if (task.assignees && Array.isArray(task.assignees)) {
                        return task.assignees.some(assignee =>
                            this.selectedUsers.includes(assignee.user_id)
                        );
                    }
                    return false;
                });
            }

            // Companies filter
            if (this.selectedCompanies.length > 0) {
                filtered = filtered.filter(task =>
                    this.selectedCompanies.includes(task.company_id)
                );
            }

            // Due date filter
            if (this.selectedDueDateFilter) {
                const today = new Date();
                today.setHours(0, 0, 0, 0); // Start of today
                filtered = filtered.filter(task => {
                    if (!task.due_date) return false;
                    const dueDate = new Date(task.due_date);
                    dueDate.setHours(0, 0, 0, 0); // Start of due date

                    switch (this.selectedDueDateFilter) {
                        case 'overdue':
                            // Tasks are overdue only if due date is before today (not including today)
                            return dueDate < today && !['completed', 'cancelled'].includes(task.status);
                        case 'today':
                            return dueDate.toDateString() === today.toDateString();
                        case 'week': {
                            const weekFromNow = new Date(today);
                            weekFromNow.setDate(today.getDate() + 7);
                            return dueDate >= today && dueDate <= weekFromNow;
                        }
                        case 'month': {
                            const monthFromNow = new Date(today);
                            monthFromNow.setMonth(today.getMonth() + 1);
                            return dueDate >= today && dueDate <= monthFromNow;
                        }
                        default:
                            return true;
                    }
                });
            }

            // Privacy filter for restricted users' created tasks
            filtered = filtered.filter(task => this.canViewCreatedTask(task));

            return filtered;
        },

        // Statistics - Always use injected backend totals, filters only affect task tables
        totalTasks() {
            const stats = this.overviewStats();
            return stats ? stats.total_tasks : 0;
        },
        inProgressTasks() {
            const stats = this.overviewStats();
            return stats ? stats.in_progress_tasks : 0;
        },
        completedTasks() {
            const stats = this.overviewStats();
            return stats ? stats.completed_tasks : 0;
        },
        overdueTasks() {
            const stats = this.overviewStats();
            return stats ? stats.overdue_tasks : 0;
        },

        // Check if any filters are active
        hasActiveFilters() {
            return this.searchQuery ||
                this.selectedRoles.length > 0 ||
                this.selectedStatuses.length > 0 ||
                this.selectedUsers.length > 0 ||
                this.selectedCompanies.length > 0 ||
                this.selectedDueDateFilter;
        },

        // Filtered tasks per role
        filteredTasksByRole() {
            const result = {};
            const isAdmin = [1, 11].includes(this.get_user?.role_id);
            
            // For admin users, prioritize injected roleTasksMap from DefaultLayout
            if (isAdmin) {
                const injectedTasks = this.roleTasksMap();
                Object.keys(injectedTasks).forEach(roleIdStr => {
                    // Convert string key to number for consistent access
                    const roleId = parseInt(roleIdStr) || roleIdStr;
                    const tasks = injectedTasks[roleIdStr] || [];
                    if (tasks.length > 0) {
                        result[roleId] = this.hasActiveFilters ? this.applyTaskFilters(tasks) : tasks;
                    }
                });
            }
            
            // Also include any locally loaded tasks (for non-admin or fallback)
            Object.keys(this.localRoleTasksMap).forEach(roleIdStr => {
                const roleId = parseInt(roleIdStr) || roleIdStr;
                if (!result[roleId]) {
                    const tasks = this.localRoleTasksMap[roleIdStr] || [];
                    if (tasks.length > 0) {
                        result[roleId] = this.hasActiveFilters ? this.applyTaskFilters(tasks) : tasks;
                    }
                }
            });
            
            return result;
        },
    },
    methods: {

        async loadTasksForRole(roleId) {
            // Skip if already loaded or currently loading
            if (this.localRoleTasksMap[roleId] || this.loadingRoles[roleId]) {
                console.log(`[OverviewComponent] Tasks already loaded for role ${roleId}`);
                return;
            }

            try {
                this.$set(this.loadingRoles, roleId, true);
                console.log('[OverviewComponent] Loading tasks for role:', roleId);
                const response = await this.$axios.get(`api/tasks/get_tasks_by_role/${roleId}`, {});
                
                if (response.data.response) {
                    // Use Object.freeze to prevent deep reactivity and improve performance
                    const tasks = Object.freeze(response.data.data || []);
                    this.$set(this.localRoleTasksMap, roleId, tasks);
                    console.log(`[OverviewComponent] Loaded ${tasks.length} tasks for role ${roleId}`);
                    
                    // Mark as ready for rendering immediately
                    this.$set(this.renderingRoles, roleId, true);
                } else {
                    console.warn(`[OverviewComponent] Failed to load tasks for role ${roleId}:`, response.data.message);
                    this.$set(this.localRoleTasksMap, roleId, []);
                    this.$set(this.renderingRoles, roleId, true);
                }
            } catch (error) {
                console.error(`[OverviewComponent] Error loading tasks for role ${roleId}:`, error);
                this.$set(this.localRoleTasksMap, roleId, []);
                this.$set(this.renderingRoles, roleId, true);
            } finally {
                this.$set(this.loadingRoles, roleId, false);
            }
        },

        onPanelChange(roleId, isOpen) {
            if (isOpen) {
                // Set loading state immediately for instant visual feedback
                this.$set(this.loadingRoles, roleId, true);
                
                const isAdmin = [1, 11].includes(this.get_user?.role_id);
                
                if (isAdmin) {
                    // Admin users: check if data is pre-loaded from DefaultLayout
                    const preloadedTasks = this.roleTasksMap();
                    if (preloadedTasks[roleId]) {
                        console.log(`[OverviewComponent] Using ${preloadedTasks[roleId].length} pre-loaded tasks for role ${roleId}`);
                        // Clear loading state immediately since data is ready
                        this.$set(this.loadingRoles, roleId, false);
                        this.$set(this.renderingRoles, roleId, true);
                    } else if (Object.keys(preloadedTasks).length === 0) {
                        // DefaultLayout hasn't loaded yet, trigger refresh
                        console.log('[OverviewComponent] Triggering role tasks refresh from DefaultLayout');
                        this.refreshRoleTasksMap().then(() => {
                            this.$set(this.loadingRoles, roleId, false);
                            this.$set(this.renderingRoles, roleId, true);
                        }).catch(e => {
                            console.error('Error refreshing role tasks:', e);
                            // Fallback to individual load
                            this.loadTasksForRole(roleId);
                        });
                    } else {
                        // Role not in preloaded data, load individually
                        this.loadTasksForRole(roleId);
                    }
                } else {
                    // Non-admin users: load individual role tasks
                    this.loadTasksForRole(roleId);
                }
            }
        },

        async loadUsers() {
            try {
                // Users are now provided via injection from DefaultLayout
                await this.refreshUsers();
            } catch (error) {
                console.error('Error loading users:', error);
            }
        },

        // Moved from misc.service.js
        async getStatus() {
            try {
                const response = await this.$axios.get('/api/misc/get_status')
                const { data } = response
                this.$store.commit('tasks/set_statuses', data)
            } catch (error) {
                console.log('error on fetching status - service layer', error)
            }
        },

        async getPriorities() {
            try {
                const response = await this.$axios.get('/api/misc/get_prority')
                const { data } = response
                this.$store.commit('tasks/set_priorities', data)
            } catch (error) {
                console.log('error on fetching priorities - service layer', error)
            }
        },

        applyTaskFilters(tasks) {
            // Apply all filters to task array
            let filtered = [...tasks];

            // Search filter
            if (this.searchQuery) {
                const query = this.searchQuery.toLowerCase();
                filtered = filtered.filter(task =>
                    task.name?.toLowerCase().includes(query) ||
                    task.description?.toLowerCase().includes(query)
                );
            }

            // Status filter
            if (this.selectedStatuses.length > 0) {
                filtered = filtered.filter(task =>
                    this.selectedStatuses.includes(task.status_id)
                );
            }

            // Users filter
            if (this.selectedUsers.length > 0) {
                filtered = filtered.filter(task => {
                    if (task.assignees && Array.isArray(task.assignees)) {
                        return task.assignees.some(assignee =>
                            this.selectedUsers.includes(assignee.user_id)
                        );
                    }
                    return false;
                });
            }

            // Companies filter
            if (this.selectedCompanies.length > 0) {
                filtered = filtered.filter(task =>
                    this.selectedCompanies.includes(task.company_id)
                );
            }

            // Due date filter
            if (this.selectedDueDateFilter) {
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                filtered = filtered.filter(task => {
                    if (!task.due_date) return false;
                    const dueDate = new Date(task.due_date);
                    dueDate.setHours(0, 0, 0, 0);

                    switch (this.selectedDueDateFilter) {
                        case 'overdue':
                            return dueDate < today;
                        case 'today':
                            return dueDate.toDateString() === today.toDateString();
                        case 'week': {
                            const weekFromNow = new Date(today);
                            weekFromNow.setDate(today.getDate() + 7);
                            return dueDate >= today && dueDate <= weekFromNow;
                        }
                        case 'month': {
                            const monthFromNow = new Date(today);
                            monthFromNow.setMonth(today.getMonth() + 1);
                            return dueDate >= today && dueDate <= monthFromNow;
                        }
                        default:
                            return true;
                    }
                });
            }

            return filtered;
        },

        getTeamTaskCount(roleId) {
            // Use by_role data from overview stats API
            const stats = this.overviewStats();
            console.log('[getTeamTaskCount] roleId:', roleId, 'stats:', stats);
            if (!stats || !stats.by_role) {
                console.warn('[getTeamTaskCount] No stats or by_role data available');
                return 0;
            }
            
            const roleData = stats.by_role.find(r => r.role_id === roleId);
            console.log('[getTeamTaskCount] roleData for', roleId, ':', roleData);
            return roleData ? roleData.total_count : 0;
        },

        getTeamColor(roleId) {
            const colors = ['primary', 'secondary', 'accent', 'success', 'warning', 'error', 'info'];
            return colors[roleId % colors.length];
        },

        getTeamIcon(roleId) {
            const icons = [
                'mdi-account-supervisor',
                'mdi-cog',
                'mdi-palette',
                'mdi-code-tags',
                'mdi-briefcase',
                'mdi-bullhorn',
            ];
            return icons[roleId % icons.length];
        },

        getTeamInProgressCount(roleId) {
            // Use by_role data from overview stats API
            const stats = this.overviewStats();
            if (!stats || !stats.by_role) {
                console.warn('[getTeamInProgressCount] No stats or by_role data');
                return 0;
            }
            
            const roleData = stats.by_role.find(r => r.role_id === roleId);
            return roleData ? roleData.in_progress_count : 0;
        },

        getTeamProgress(roleId) {
            // Calculate progress as percentage of tasks in progress out of total tasks
            const stats = this.overviewStats();
            if (!stats || !stats.by_role) return 0;
            
            const roleData = stats.by_role.find(r => r.role_id === roleId);
            if (!roleData || roleData.total_count === 0) return 0;
            
            // Progress = (in_progress_count / total_count) * 100
            return Math.round((roleData.in_progress_count / roleData.total_count) * 100);
        },

        navigateToTeam(roleId) {
            // Navigate to team details page
            this.$router.push(`/team/${roleId}`);
        },

        // Task creation methods
        openCreateTaskDialog(roleId = null) {
            this.selected_role_for_task = roleId;
            
            // Get members for the selected role
            if (roleId) {
                const role = this.filteredRoles.find(r => r.id === roleId);
                if (role && role.managers && role.managers.length > 0) {
                    this.selected_role_managers = role.managers.map(m => m.user_id);
                } else {
                    this.selected_role_managers = [];
                }
            } else {
                this.selected_role_managers = [];
            }
            
            this.create_task_dialog = true;
        },

        closeCreateTaskDialog() {
            this.create_task_dialog = false;
            this.selected_role_for_task = null;
            this.selected_role_managers = [];
        },

        onTasksCreated() {
            // Refresh overview tasks when new tasks are created
            if (this.refreshOverviewTasks) {
                this.refreshOverviewTasks().catch(error => {
                    console.error('Error refreshing overview tasks:', error);
                });
            }
        },

        async updateTask() {
            try {
                // Refresh handled by optimistic updates in DefaultLayout
                if (this.refreshOverviewTasks) {
                    await this.refreshOverviewTasks();
                }
            } catch (error) {
                console.error('Error refreshing tasks:', error);
            }
        },

        async deleteTask() {
            try {
                // Refresh handled by optimistic updates in DefaultLayout
                if (this.refreshOverviewTasks) {
                    await this.refreshOverviewTasks();
                }
            } catch (error) {
                console.error('Error refreshing tasks:', error);
            }
        },

        getTasks() {
            // Handle getTasks event from UnifiedTreeViewDashboard
            if (this.refreshOverviewTasks) {
                this.refreshOverviewTasks().catch(error => {
                    console.error('Error refreshing overview tasks:', error);
                });
            }
        },

        updateTaskData({ taskId }) {
            // Tasks are already updated in DefaultLayout through optimistic updates
            // This method is kept for compatibility but doesn't need to do anything
            console.log(`[OverviewComponent] Task ${taskId} updated via DefaultLayout`);
        },

        // Filter methods
        clearAllFilters() {
            this.searchQuery = '';
            this.selectedRoles = [];
            this.selectedStatuses = [];
            this.selectedUsers = [];
            this.selectedCompanies = [];
            this.selectedDueDateFilter = null;
        },

        // Pre-render optimization method
        async preRenderAllPanels() {
            console.log('[OverviewComponent] Pre-rendering all panels to eliminate expansion lag');
            
            const isAdmin = [1, 11].includes(this.get_user?.role_id);
            const roles = this.filteredRoles;
            
            if (roles.length === 0) {
                console.log('[OverviewComponent] No roles to pre-render');
                return;
            }

            // Expand all panels programmatically
            for (const role of roles) {
                // Set expanded state
                this.$set(this.expandedPanels, role.id, [0]); // Expand first panel
                
                // Trigger the panel change logic to load/render tasks
                if (isAdmin) {
                    const preloadedTasks = this.roleTasksMap();
                    if (preloadedTasks[role.id]) {
                        this.$set(this.loadingRoles, role.id, false);
                        this.$set(this.renderingRoles, role.id, true);
                    }
                } else {
                    // For non-admin, trigger load
                    if (!this.localRoleTasksMap[role.id] && !this.loadingRoles[role.id]) {
                        await this.loadTasksForRole(role.id);
                    }
                }
            }

            // Wait for all renders to complete
            await this.$nextTick();
            await new Promise(resolve => setTimeout(resolve, 500)); // Allow time for rendering

            // Collapse all panels
            console.log('[OverviewComponent] Collapsing all panels after pre-render');
            for (const role of roles) {
                this.$set(this.expandedPanels, role.id, []); // Collapse
            }

            console.log('[OverviewComponent] Pre-rendering complete - panels ready for instant expansion');
        },
        
        // Role Member methods
        async loadRoleManagers() {
            try {
                // Use injected provider to get members from DefaultLayout
                const members = this.allManagers();
                
                console.log('All members data:', members);
                
                // Convert array to object mapping user_role_id -> user_id
                const managersMap = {};
                if (members && members.length > 0) {
                    members.forEach(item => {
                        // Map user_role_id to user_id based on the API response structure
                        managersMap[item.user_role_id] = item.user_id;
                    });
                }
                this.roleManagers = managersMap;
                console.log('Mapped role members:', this.roleManagers);
            } catch (error) {
                console.error('Error loading role members:', error);
            }
        },
        
        // getRoleManager(roleId) {
        //     return this.roleManagers[roleId] || null;
        // },

        // getManagerName(roleId) {
        //     const managerId = this.roleManagers[roleId];
        //     console.log(`Getting manager name for role ${roleId}, managerId: ${managerId}`);
        //     if (!managerId) return null;
        //     const manager = this.usersSelect.find(u => u.id === managerId);
        //     return manager ? manager.name : null;
        // },

        selectedRoleName() {
            if (!this.selectedRoleId) return '';
            const roles = this.get_roles || [];
            const role = roles.find(r => r.id === this.selectedRoleId);
            return role ? role.name : '';
        },

        openManagerDialog(roleId) {
            this.selectedRoleId = roleId;
            
            // Find the role in filteredRoles to get current managers
            const role = this.filteredRoles.find(r => r.id === roleId);
            if (role && role.managers && role.managers.length > 0) {
                this.selectedManagerIds = role.managers.map(m => m.user_id);
                console.log('[openManagerDialog] Pre-populated with:', this.selectedManagerIds);
            } else {
                this.selectedManagerIds = [];
                console.log('[openManagerDialog] No existing members');
            }
            
            console.log('[openManagerDialog] selectedManagerIds type:', typeof this.selectedManagerIds, 'isArray:', Array.isArray(this.selectedManagerIds));
            this.managerDialog = true;
        },

        closeManagerDialog() {
            this.managerDialog = false;
            this.selectedRoleId = null;
            this.selectedManagerIds = [];
        },

        async saveManagers() {
            if (!this.selectedRoleId) return;
            
            console.log('[saveManagers] selectedManagerIds:', JSON.stringify(this.selectedManagerIds));
            console.log('[saveManagers] selectedManagerIds length:', this.selectedManagerIds.length);
            console.log('[saveManagers] selectedManagerIds is array:', Array.isArray(this.selectedManagerIds));
            await this.updateRoleManagers(this.selectedRoleId, this.selectedManagerIds);
            this.closeManagerDialog();
        },
        
        async assignManagerToRole(roleId, managerIds) {
            try {
                // Support both single and multiple manager IDs
                const userIds = Array.isArray(managerIds) ? managerIds : [managerIds];
                
                console.log('[assignManagerToRole] Sending payload - role_id:', roleId, 'user_ids:', userIds);
                
                const response = await this.$axios.post('api/users/assign_manager', {
                    role_id: roleId,
                    user_ids: userIds  // Send as array
                });

                console.log('[assignManagerToRole] Response:', response.data);

                if (response.data.response) {
                    // Refresh managers list to sync with backend
                    await this.refreshManagers();
                    return { success: true, data: response.data };
                }
                return { success: false, message: response.data.message };
            } catch (error) {
                console.error('Error assigning manager', error);
                return { success: false, message: 'Failed to assign manager' };
            }
        },

        async removeManagerFromRole(roleId, memberIds) {
            try {
                // Support both single and multiple member IDs
                const memberIdArray = Array.isArray(memberIds) ? memberIds : [memberIds];
                
                console.log('[removeManagerFromRole] Sending payload - user_role_id:', roleId, 'user_ids:', memberIdArray);
                
                const response = await this.$axios.post('api/users/remove_manager', {
                    user_role_id: roleId,
                    user_ids: memberIdArray  // Send as array
                });

                console.log('[removeManagerFromRole] Response:', response.data);

                if (response.data.response) {
                    // Refresh managers list to sync with backend
                    await this.refreshManagers();
                    return { success: true, data: response.data };
                }
                return { success: false, message: response.data.message };
            } catch (error) {
                console.error('Error removing manager', error);
                return { success: false, message: 'Failed to remove manager' };
            }
        },

        async getManagersByRoleId(roleId) {
            try {
                const response = await this.$axios.get(`api/users/get_managers_by_role/${roleId}`);
                if (response.data.response) {
                    return response.data.data || [];
                }
                return [];
            } catch (error) {
                console.error(`Error fetching managers for role ${roleId}`, error);
                return [];
            }
        },

        async updateRoleManagers(roleId, managerIds) {
            try {
                console.log('[updateRoleManagers] roleId:', roleId, 'managerIds:', managerIds);
                
                // Get current managers for this role to check if anything changed
                const role = this.filteredRoles.find(r => r.id === roleId);
                const currentManagerIds = role && role.managers ? role.managers.map(m => m.user_id) : [];
                
                console.log('[updateRoleManagers] currentManagerIds:', currentManagerIds);
                
                // Check if there are any changes
                const hasChanges = JSON.stringify(currentManagerIds.sort()) !== JSON.stringify(managerIds.sort());
                
                if (!hasChanges) {
                    this.$toast.info('No changes to save');
                    return;
                }
                
                // Send the complete final list - backend will handle removing managers not in the list
                const result = await this.assignManagerToRole(roleId, managerIds);
                
                if (result.success) {
                    this.$toast.success('Managers updated successfully');
                    // Reload managers to sync with backend
                    await this.refreshManagers();
                    await this.loadRoleManagers();
                } else {
                    this.$toast.error('Failed to update managers');
                    // Reload to sync with actual backend state
                    await this.refreshManagers();
                    await this.loadRoleManagers();
                }
            } catch (error) {
                console.error('Error updating role managers:', error);
                this.$toast.error('Failed to update managers');
                // Reload to sync with actual backend state
                await this.refreshManagers();
                await this.loadRoleManagers();
            }
        },
        
        getInitials(name) {
            if (!name) return '?';
            return name
                .split(' ')
                .map(word => word[0])
                .join('')
                .toUpperCase()
                .substring(0, 2);
        },
        
        async refreshData() {
            this.refreshing = true;
            try {
                await Promise.all([
                    this.refreshOverviewTasks(),
                    this.refreshUsers(),
                    this.getStatus(),
                    this.getPriorities(),
                    this.refreshCompanies(),
                    this.refreshManagers(), // Refresh managers from backend
                ]);
                // Reload role managers after refreshing
                await this.loadRoleManagers();
                this.$toast.success('Data refreshed successfully');
            } catch (error) {
                console.error('Error refreshing data:', error);
                this.$toast.error('Failed to refresh data');
            } finally {
                this.refreshing = false;
            }
        },

        async sendOverdueReminders() {
            this.sendingOverdueReminders = true;
            try {
                const response = await this.$axios.post('api/tasks/send_overdue_reminders');
                if (response.data.response) {
                    this.$toast.success(response.data.message);
                } else {
                    this.$toast.error(response.data.message || 'Failed to send reminders');
                }
            } catch (error) {
                console.error('Error sending overdue reminders:', error);
                this.$toast.error('Failed to send overdue reminders');
            } finally {
                this.sendingOverdueReminders = false;
            }
        },
        canViewCreatedTask(task) {
            if (!task || !task.user_id) return true;

            const currentUser = this.get_user;
            if (!currentUser) return false;

            const currentUserId = currentUser.id;
            const taskCreatorId = task.user_id;
            const restrictedUserIds = [2, 4];

            // If the task creator is not in the restricted list, everyone can see the task
            if (!restrictedUserIds.includes(taskCreatorId)) {
                return true;
            }

            // If task was created by user 2
            if (taskCreatorId === 2) {
                // Only user 2 can see tasks they created
                return currentUserId === 2;
            }

            // If task was created by user 4
            if (taskCreatorId === 4) {
                // User 2 can see tasks created by user 4, and user 4 can see their own created tasks
                return currentUserId === 2 || currentUserId === 4;
            }

            return false;
        },
        
        removeManager(managerId) {
            console.log('[removeManager] Removing manager:', managerId);
            this.selectedManagerIds = this.selectedManagerIds.filter(id => id !== managerId);
        },
    },
};
</script>

<style lang="scss" scoped>
.overview-header {
    .filter-card {
        border-radius: 8px;
    }
}

.teams-container {
    .teams-grid {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
        gap: 24px;
        margin-bottom: 24px;
    }

    .team-card-wrapper {
        .team-card {
            border-radius: 12px;
            overflow: hidden;
            cursor: pointer;
            transition: all 0.3s ease;
            border: 1px solid #e0e0e0;

            &:hover {
                transform: translateY(-4px);
                box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12) !important;
            }

            .team-card-header {
                padding: 20px;
                background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);

                .team-card-title {
                    font-size: 18px;
                    font-weight: 600;
                    color: #212121;
                    margin: 0;
                }

                .team-card-subtitle {
                    font-size: 13px;
                    color: #666;
                }

                .status-chip {
                    &.active-chip {
                        background-color: #4CAF50 !important;
                        color: white !important;
                    }
                }
            }

            .team-card-stats {
                .stat-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 12px;

                    &:last-child {
                        margin-bottom: 0;
                    }

                    .stat-label {
                        font-size: 14px;
                        color: #666;
                    }

                    .stat-value {
                        font-size: 16px;
                        font-weight: 600;
                        color: #212121;
                    }
                }
            }

            .team-card-progress {
                .progress-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;

                    .progress-label {
                        font-size: 13px;
                        color: #666;
                        font-weight: 500;
                    }

                    .progress-percentage {
                        font-size: 14px;
                        font-weight: 600;
                        color: #4285F4;
                    }
                }

                .progress-bar-container {
                    height: 8px;
                    background-color: #E8F0FE;
                    border-radius: 4px;
                    overflow: hidden;

                    .progress-bar-fill {
                        height: 100%;
                        background: linear-gradient(90deg, #4285F4 0%, #1976D2 100%);
                        border-radius: 4px;
                        transition: width 0.3s ease;
                    }
                }
            }

            .team-card-footer {
                .footer-section {
                    display: flex;
                    align-items: center;
                    margin-bottom: 8px;

                    .footer-label {
                        font-size: 13px;
                        color: #666;
                        font-weight: 500;
                    }
                }

                .manager-avatars {
                    display: flex;
                    gap: 6px;

                    .manager-avatar {
                        border: 2px solid white;
                        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12);
                    }
                }

                .no-managers-text {
                    padding: 8px 0;
                }
            }

            .team-card-actions {
                display: flex;
                justify-content: flex-end;
                gap: 8px;
                background-color: #fafafa;

                .action-btn {
                    text-transform: none;
                    font-weight: 500;
                }
            }
        }
    }

    .empty-state-container {
        text-align: center;
        padding: 80px 24px;

        .empty-title {
            font-size: 20px;
            font-weight: 600;
            color: #666;
            margin-bottom: 8px;
        }

        .empty-description {
            font-size: 14px;
            color: #999;
        }
    }
}

// Statistics cards styling
.v-card {
    transition: all 0.3s ease;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
    }
}

// Filter styling
.filter-card {

    .v-select,
    .v-text-field {
        .v-chip {
            background-color: #e3f2fd !important;
            color: #1976d2 !important;
            font-weight: 500;
        }
    }
}

// Responsive adjustments
@media (max-width: 768px) {
    .overview-header {
        .d-flex.justify-space-between {
            flex-direction: column;
            align-items: flex-start;

            .v-btn {
                margin-top: 16px;
                align-self: stretch;
            }
        }
    }

    .team-header {
        .d-flex.justify-space-between {
            flex-direction: column;
            align-items: flex-start;

            .d-flex.align-center:last-child {
                margin-top: 12px;
                width: 100%;
                justify-content: space-between;
            }
        }
    }
}
</style>